import {
  Users2,
  ClipboardList,
  HelpCircle,
  UserCheck,
  RefreshCw,
  Bookmark,
  Star,
  Clock,
  CheckSquare,
  Target,
  HelpingHand,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import Markdown from "react-markdown";
import { useRef, useState } from "react";
import CardImage from "../tool_components/CardImage";
import { Button, Text } from "@core/index";
import CollaborativeCardPDF from "./CollaborativeCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { CollaborativeGroupWorkResponseDto } from "@packages/api/madlenAPI.schemas";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetCollaborativeGroupWorkQueryKey,
  getGetUserCollaborativeGroupWorksQueryKey,
  updateCollaborativeGroupWork,
} from "@packages/api/madlenAPI";

export interface CollaborativeCardProps {
  collaborative: CollaborativeGroupWorkResponseDto;
  isEditable?: boolean;
  onCreateAgain?: () => void;
}

type TabType =
  | "groupRoles"
  | "processSteps"
  | "reflectionQuestions"
  | "peerEvaluation";

const CollaborativeCard = ({
  collaborative,
  onCreateAgain,
  isEditable = false,
}: CollaborativeCardProps) => {
  const finalImageUrl = useImagePdf(collaborative.imageUrl);
  const [activeTab, setActiveTab] = useState<TabType>("groupRoles");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();
  const contentLanguage = collaborative.contentLanguage || "tr";

  const tabs = [
    {
      id: "groupRoles",
      icon: Users2,
      label: tC(contentLanguage, "collaborative.tabs.groupRoles"),
    },
    {
      id: "processSteps",
      icon: ClipboardList,
      label: tC(contentLanguage, "collaborative.tabs.processSteps"),
    },
    {
      id: "reflectionQuestions",
      icon: HelpCircle,
      label: tC(contentLanguage, "collaborative.tabs.reflectionQuestions"),
    },
    {
      id: "peerEvaluation",
      icon: UserCheck,
      label: tC(contentLanguage, "collaborative.tabs.peerEvaluation"),
    },
  ];

  const renderLabel = (icon: React.ReactNode, text: string) => (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5E0C3]/30 dark:bg-[#6D4C3D]/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
      {icon}
      <span className="font-medium text-[#8D6B4E] dark:text-[#C8B08D]">
        {text}
      </span>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "groupRoles":
        return (
          <div className="space-y-6">
            {collaborative.groupRoles?.map((role, idx) => (
              <div
                key={idx}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl shadow-lg transition-all duration-300 shadow-[#BFA58A]/10 hover:shadow-[#BFA58A]/20"
              >
                <h5 className="font-medium text-lg mb-6 text-[#8D6B4E] dark:text-[#C8B08D] pb-3 border-b border-[#BFA58A]/20">
                  <EditableMarkdown
                    value={role.title}
                    isEditable={isEditable ?? true}
                    handleSave={async (newValue) => {
                      if (!collaborative.groupRoles) return;

                      const updatedRoles = [...collaborative.groupRoles];
                      updatedRoles[idx] = {
                        ...updatedRoles[idx],
                        title: newValue,
                      };

                      await updateCollaborativeGroupWork(collaborative.id, {
                        groupRoles: updatedRoles,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserCollaborativeGroupWorksQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetCollaborativeGroupWorkQueryKey(
                          collaborative.id,
                        ),
                      });
                    }}
                  />
                </h5>
                <div className="space-y-6">
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    {renderLabel(
                      <Bookmark
                        size={18}
                        className="text-[#8D6B4E] dark:text-[#C8B08D]"
                      />,
                      tC(contentLanguage, "collaborative.responsibilities"),
                    )}
                    <Text
                      variant="p"
                      className="mt-3 text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={role.responsibilities}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.groupRoles) return;

                          const updatedRoles = [...collaborative.groupRoles];
                          updatedRoles[idx] = {
                            ...updatedRoles[idx],
                            responsibilities: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            groupRoles: updatedRoles,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    {renderLabel(
                      <Star
                        size={18}
                        className="text-[#8D6B4E] dark:text-[#C8B08D]"
                      />,
                      tC(contentLanguage, "collaborative.skillsDeveloped"),
                    )}
                    <Text
                      variant="p"
                      className="mt-3 text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={role.skills_developed}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.groupRoles) return;

                          const updatedRoles = [...collaborative.groupRoles];
                          updatedRoles[idx] = {
                            ...updatedRoles[idx],
                            skills_developed: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            groupRoles: updatedRoles,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "processSteps":
        return (
          <div className="space-y-6">
            {collaborative.processSteps?.map((step, idx) => (
              <div
                key={idx}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl shadow-lg transition-all duration-300 shadow-[#BFA58A]/10 hover:shadow-[#BFA58A]/20"
              >
                <h5 className="font-medium text-lg mb-6 text-[#8D6B4E] dark:text-[#C8B08D] pb-3 border-b border-[#BFA58A]/20">
                  {tC(contentLanguage, "collaborative.step")} {step.step_number}
                  :{" "}
                  <EditableMarkdown
                    value={step.title}
                    isEditable={isEditable ?? true}
                    handleSave={async (newValue) => {
                      if (!collaborative.processSteps) return;

                      const updatedSteps = [...collaborative.processSteps];
                      updatedSteps[idx] = {
                        ...updatedSteps[idx],
                        title: newValue,
                      };

                      await updateCollaborativeGroupWork(collaborative.id, {
                        processSteps: updatedSteps,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserCollaborativeGroupWorksQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetCollaborativeGroupWorkQueryKey(
                          collaborative.id,
                        ),
                      });
                    }}
                  />
                </h5>
                <div className="space-y-6">
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    <Text
                      variant="p"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={step.description}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.processSteps) return;

                          const updatedSteps = [...collaborative.processSteps];
                          updatedSteps[idx] = {
                            ...updatedSteps[idx],
                            description: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            processSteps: updatedSteps,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                      {renderLabel(
                        <Clock
                          size={18}
                          className="text-[#8D6B4E] dark:text-[#C8B08D]"
                        />,
                        tC(contentLanguage, "collaborative.timeEstimate"),
                      )}
                      <Text
                        variant="p"
                        className="mt-3 text-gray-700 dark:text-gray-300"
                      >
                        <EditableMarkdown
                          value={step.time_estimate}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!collaborative.processSteps) return;

                            const updatedSteps = [
                              ...collaborative.processSteps,
                            ];
                            updatedSteps[idx] = {
                              ...updatedSteps[idx],
                              time_estimate: newValue,
                            };

                            await updateCollaborativeGroupWork(
                              collaborative.id,
                              {
                                processSteps: updatedSteps,
                              },
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCollaborativeGroupWorksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCollaborativeGroupWorkQueryKey(
                                collaborative.id,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                    <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                      {renderLabel(
                        <CheckSquare
                          size={18}
                          className="text-[#8D6B4E] dark:text-[#C8B08D]"
                        />,
                        tC(contentLanguage, "collaborative.deliverables"),
                      )}
                      <Text
                        variant="p"
                        className="mt-3 text-gray-700 dark:text-gray-300"
                      >
                        <EditableMarkdown
                          value={step.deliverables}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!collaborative.processSteps) return;

                            const updatedSteps = [
                              ...collaborative.processSteps,
                            ];
                            updatedSteps[idx] = {
                              ...updatedSteps[idx],
                              deliverables: newValue,
                            };

                            await updateCollaborativeGroupWork(
                              collaborative.id,
                              {
                                processSteps: updatedSteps,
                              },
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCollaborativeGroupWorksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCollaborativeGroupWorkQueryKey(
                                collaborative.id,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "reflectionQuestions":
        return (
          <div className="space-y-6">
            {collaborative.reflectionQuestions?.map((item, idx) => (
              <div
                key={idx}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl shadow-lg transition-all duration-300 shadow-[#BFA58A]/10 hover:shadow-[#BFA58A]/20"
              >
                <div className="space-y-6">
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    {renderLabel(
                      <HelpCircle
                        size={18}
                        className="text-[#8D6B4E] dark:text-[#C8B08D]"
                      />,
                      tC(contentLanguage, "collaborative.question"),
                    )}
                    <Text
                      variant="p"
                      className="mt-3 text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={item.question}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.reflectionQuestions) return;

                          const updatedQuestions = [
                            ...collaborative.reflectionQuestions,
                          ];
                          updatedQuestions[idx] = {
                            ...updatedQuestions[idx],
                            question: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            reflectionQuestions: updatedQuestions,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    {renderLabel(
                      <Target
                        size={18}
                        className="text-[#8D6B4E] dark:text-[#C8B08D]"
                      />,
                      tC(contentLanguage, "collaborative.purpose"),
                    )}
                    <Text
                      variant="p"
                      className="mt-3 text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={item.purpose}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.reflectionQuestions) return;

                          const updatedQuestions = [
                            ...collaborative.reflectionQuestions,
                          ];
                          updatedQuestions[idx] = {
                            ...updatedQuestions[idx],
                            purpose: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            reflectionQuestions: updatedQuestions,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "peerEvaluation":
        return (
          <div className="space-y-6">
            {collaborative.peerEvaluationQuestions?.map((item, idx) => (
              <div
                key={idx}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl shadow-lg transition-all duration-300 shadow-[#BFA58A]/10 hover:shadow-[#BFA58A]/20"
              >
                <div className="space-y-6">
                  <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                    {renderLabel(
                      <HelpCircle
                        size={18}
                        className="text-[#8D6B4E] dark:text-[#C8B08D]"
                      />,
                      tC(contentLanguage, "collaborative.question"),
                    )}
                    <Text
                      variant="p"
                      className="mt-3 text-gray-700 dark:text-gray-300"
                    >
                      <EditableMarkdown
                        value={item.question}
                        isEditable={isEditable ?? true}
                        handleSave={async (newValue) => {
                          if (!collaborative.peerEvaluationQuestions) return;

                          const updatedQuestions = [
                            ...collaborative.peerEvaluationQuestions,
                          ];
                          updatedQuestions[idx] = {
                            ...updatedQuestions[idx],
                            question: newValue,
                          };

                          await updateCollaborativeGroupWork(collaborative.id, {
                            peerEvaluationQuestions: updatedQuestions,
                          });

                          await queryClient.invalidateQueries({
                            queryKey:
                              getGetUserCollaborativeGroupWorksQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetCollaborativeGroupWorkQueryKey(
                              collaborative.id,
                            ),
                          });
                        }}
                      />
                    </Text>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                      {renderLabel(
                        <Target
                          size={18}
                          className="text-[#8D6B4E] dark:text-[#C8B08D]"
                        />,
                        tC(contentLanguage, "collaborative.criteria"),
                      )}
                      <Text
                        variant="p"
                        className="mt-3 text-gray-700 dark:text-gray-300"
                      >
                        <EditableMarkdown
                          value={item.criteria}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!collaborative.peerEvaluationQuestions) return;

                            const updatedQuestions = [
                              ...collaborative.peerEvaluationQuestions,
                            ];
                            updatedQuestions[idx] = {
                              ...updatedQuestions[idx],
                              criteria: newValue,
                            };

                            await updateCollaborativeGroupWork(
                              collaborative.id,
                              {
                                peerEvaluationQuestions: updatedQuestions,
                              },
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCollaborativeGroupWorksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCollaborativeGroupWorkQueryKey(
                                collaborative.id,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                    <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/10 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                      {renderLabel(
                        <HelpingHand
                          size={18}
                          className="text-[#8D6B4E] dark:text-[#C8B08D]"
                        />,
                        tC(contentLanguage, "collaborative.scale"),
                      )}
                      <Text
                        variant="p"
                        className="mt-3 text-gray-700 dark:text-gray-300"
                      >
                        <EditableMarkdown
                          value={item.scale}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!collaborative.peerEvaluationQuestions) return;

                            const updatedQuestions = [
                              ...collaborative.peerEvaluationQuestions,
                            ];
                            updatedQuestions[idx] = {
                              ...updatedQuestions[idx],
                              scale: newValue,
                            };

                            await updateCollaborativeGroupWork(
                              collaborative.id,
                              {
                                peerEvaluationQuestions: updatedQuestions,
                              },
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCollaborativeGroupWorksQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCollaborativeGroupWorkQueryKey(
                                collaborative.id,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div
      data-testid="collaborative-card"
      className="min-h-screen bg-gradient-to-b from-[#F5E0C3]/20 to-white dark:from-[#6D4C3D]/20 dark:to-gray-900"
    >
      <div className="container max-w-7xl mx-auto px-4 py-8">
        {collaborative.imageUrl && (
          <CardImage
            createdAt={collaborative.createdAt}
            imageUrl={collaborative.imageUrl}
            videoUrl={collaborative.videoUrl}
            courseName={collaborative.courseName}
            gradeLevel={collaborative.gradeLevel}
            topic={collaborative.title}
            theme="brown"
            contentLanguage={contentLanguage}
          />
        )}

        <div className="flex justify-end mb-6 gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-[#BFA58A] to-[#A07E5E] hover:from-[#BFA58A]/90 hover:to-[#A07E5E]/90"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            pdfTitle={collaborative.title || ""}
            document={
              <CollaborativeCardPDF
                collaborative={{ ...collaborative, imageUrl: finalImageUrl }}
                customizationSettings={customizationSettings}
              />
            }
            btnTheme="bg-gradient-to-r from-[#BFA58A] to-[#A07E5E]"
          />
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 shadow-[#BFA58A]/5">
          <div className="flex sticky -top-6 rounded-2xl z-10 flex-wrap md:flex-nowrap gap-2 p-4 bg-[#F5E0C3] dark:bg-[#6D4C3D]">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType);
                    scrollRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300
                    ${
                      isActive
                        ? "bg-[#BFA58A] text-white shadow-lg shadow-[#BFA58A]/30"
                        : "bg-white/50 dark:bg-gray-800/50 text-[#8D6B4E] dark:text-[#C8B08D] hover:bg-[#BFA58A]/10 hover:shadow-md shadow-sm"
                    }`}
                >
                  <Icon size={18} />
                  <span className="hidden md:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6 relative">
            <div ref={scrollRef} className="absolute -top-20 -z-10" />
            {renderTabContent()}
          </div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <CollaborativeCardPDF
            collaborative={{ ...collaborative, imageUrl: finalImageUrl }}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};

export default CollaborativeCard;
